/*
  Buchanan Albert: Find your piece (step 8).
  Search, filters, sort and "Show more" for the All products page, Women's, Men's and every
  category view. Markup: snippets/ba-find.liquid. Data: sections/ba-find-index.liquid, fetched
  through the Section Rendering API (about 25 KB compressed for the whole catalog) once per
  visit and kept in sessionStorage for 20 minutes, so every tap answers at once on a phone.

  Until the shopper searches, filters or sorts, the page's own grid stays (server rendered,
  fast first paint). The moment they do, that grid is hidden and the results grid below the
  bar shows the answer. The state lives in the address after # (q, g, c, col, size, price,
  len, sort, n), so Back from a product page returns to the same results, as many as were
  shown, at the same scroll position.

  Search understands everyday words (gown finds dresses, trainers finds sneakers, pants finds
  trousers), plurals, joined words (t shirt, tshirt) and typos (one wrong letter in a short
  word, two in a long one; fixed only into shopping words, never into a title's place name).
  A real word for something the shop does not carry (skirt, robe) is answered honestly with
  the closest category. It never ends empty: it relaxes filters, then shows the closest
  pieces, then best sellers with suggestions.
*/
(() => {
  if (window.BAFind) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CACHE_KEY = 'ba-find:index:v1';
  const TTL = 20 * 60 * 1000;
  const STEP = 24;

  /* ---------- text helpers ---------- */
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’'`]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  /* A crude stem, applied the same way to the catalog and to the shopper's words. */
  const stem = (w) => {
    /* hoodie must not stem to "hood", or a search for hoodies brought up parkas with a fur hood */
    if (/^hood(ie|y)s?$/.test(w)) return 'hoodie';
    if (w.length < 4 || /\d/.test(w)) return w;
    if (/ies$/.test(w)) w = w.slice(0, -3);
    else if (/(ss|us|is)$/.test(w)) return w;
    else if (/(s|sh|ch|x|z)es$/.test(w)) w = w.slice(0, -2);
    else if (/s$/.test(w)) w = w.slice(0, -1);
    const cut = w.replace(/(ie|y|e)$/, '');
    return cut.length >= 3 ? cut : w;
  };
  /* Damerau-Levenshtein distance with an early stop. */
  const dist = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    const d = [];
    for (let i = 0; i <= a.length; i++) {
      d[i] = [i];
    }
    for (let j = 1; j <= b.length; j++) d[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      let rowMin = Infinity;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, d[i - 2][j - 2] + 1);
        d[i][j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
    }
    return d[a.length][b.length];
  };

  /* ---------- everyday words ---------- */
  const PHRASES = [
    [/\bt shirts?\b/g, 'tshirt'],
    [/\btee shirts?\b/g, 'tshirt'],
    [/\bpolo shirts?\b/g, 'polo'],
    [/\bco ords?\b/g, 'coord'],
    [/\btwo piece\b/g, 'twopiece'],
    [/\b2 piece\b/g, 'twopiece'],
    [/\bthree piece\b/g, 'threepiece'],
    [/\b3 piece\b/g, 'threepiece'],
    [/\boff white\b/g, 'offwhite'],
    [/\bslip ons?\b/g, 'slipon'],
    [/\bflip flops?\b/g, 'sandal'],
    [/\btennis shoes?\b/g, 'sneaker'],
    [/\brunning shoes?\b/g, 'sneaker'],
    [/\bdress shoes?\b/g, 'oxford loafer'],
    [/\bdress shirts?\b/g, 'shirt'],
    [/\bdress pants?\b/g, 'trouser'],
    [/\bdinner jackets?\b/g, 'tuxedo'],
    [/\bsports? coats?\b/g, 'blazer'],
    [/\bsuit jackets?\b/g, 'blazer'],
    [/\bwedding guest\b/g, 'wedding'],
    [/\bball gowns?\b/g, 'gown'],
    [/\bfloor length\b/g, 'maxi'],
    [/\bknee length\b/g, 'midi'],
    [/\bankle boots?\b/g, 'ankle boot'],
  ];
  const SYN = {
    gown: ['gown', 'dress', 'maxi'],
    frock: ['dress'],
    dress: ['dress', 'gown', 'sundress', 'kaftan', 'caftan'],
    sundress: ['sundress', 'dress'],
    kaftan: ['kaftan', 'caftan'],
    trainer: ['trainer', 'sneaker'],
    sneaker: ['sneaker', 'trainer'],
    kick: ['sneaker'],
    runner: ['sneaker'],
    pant: ['pant', 'trouser', 'slack', 'chino', 'jogger', 'culotte', 'sweatpant'],
    trouser: ['trouser', 'pant', 'slack', 'chino'],
    slack: ['slack', 'pant', 'trouser'],
    chino: ['chino', 'trouser', 'pant'],
    jumper: ['jumper', 'sweater', 'pullover', 'knit', 'cardigan'],
    sweater: ['sweater', 'jumper', 'pullover', 'knit', 'cardigan', 'turtleneck'],
    pullover: ['pullover', 'sweater', 'jumper'],
    knit: ['knit', 'knitted', 'sweater', 'cardigan', 'jumper', 'pullover', 'turtleneck', 'ribbed'],
    knitwear: ['knit', 'knitted', 'sweater', 'cardigan', 'jumper', 'pullover', 'turtleneck', 'ribbed'],
    cardi: ['cardigan'],
    cardigan: ['cardigan', 'knit'],
    jacket: ['jacket', 'coat', 'blazer', 'bomber', 'parka', 'puffer', 'overshirt', 'shacket', 'trench'],
    coat: ['coat', 'overcoat', 'topcoat', 'trench', 'parka', 'puffer', 'jacket'],
    outerwear: ['coat', 'jacket', 'overcoat', 'trench', 'parka', 'puffer', 'bomber'],
    tshirt: ['tshirt', 'tee'],
    tee: ['tee', 'tshirt'],
    top: ['top', 'blouse', 'shirt', 'tee', 'tshirt', 'tank', 'cami', 'camisole', 'tunic', 'bodysuit', 'polo', 'henley'],
    blouse: ['blouse', 'shirt', 'top'],
    shirt: ['shirt', 'blouse', 'overshirt'],
    tank: ['tank', 'cami', 'camisole'],
    heel: ['heel', 'pump', 'stiletto'],
    pump: ['pump', 'heel'],
    stiletto: ['stiletto', 'heel', 'pump'],
    flat: ['flat', 'ballet'],
    sandal: ['sandal', 'slide', 'espadrille'],
    slider: ['slide', 'sandal'],
    boot: ['boot', 'bootie'],
    bootie: ['bootie', 'boot'],
    loafer: ['loafer', 'moccasin', 'slipon'],
    moccasin: ['moccasin', 'loafer'],
    shoe: ['shoe', 'sneaker', 'loafer', 'boot', 'bootie', 'sandal', 'heel', 'flat', 'mule', 'oxford', 'brogue', 'pump', 'slide', 'clog', 'espadrille', 'moccasin', 'slipon', 'slipper'],
    footwear: ['shoe', 'sneaker', 'loafer', 'boot', 'sandal', 'heel', 'flat', 'mule', 'oxford', 'brogue', 'pump', 'slide'],
    suit: ['suit', 'tuxedo', 'blazer', 'waistcoat'],
    tux: ['tuxedo'],
    tuxedo: ['tuxedo', 'suit'],
    blazer: ['blazer', 'suit'],
    jumpsuit: ['jumpsuit', 'romper', 'playsuit', 'overall'],
    romper: ['romper', 'playsuit', 'jumpsuit'],
    playsuit: ['playsuit', 'romper', 'jumpsuit'],
    onesie: ['jumpsuit', 'romper'],
    set: ['set', 'coord', 'twopiece', 'threepiece', 'tracksuit'],
    coord: ['coord', 'set', 'twopiece'],
    matching: ['set', 'coord', 'twopiece'],
    outfit: ['set', 'coord', 'twopiece', 'suit'],
    shade: ['sunglass'],
    sunny: ['sunglass'],
    sunnie: ['sunglass'],
    glass: ['sunglass'],
    sunglass: ['sunglass'],
    bag: ['bag', 'tote', 'handbag', 'clutch', 'purse', 'backpack'],
    purse: ['purse', 'bag', 'handbag', 'clutch', 'wallet'],
    handbag: ['handbag', 'bag', 'tote', 'clutch', 'purse'],
    vest: ['vest', 'waistcoat', 'gilet'],
    waistcoat: ['waistcoat', 'vest'],
    hoody: ['hoodie'],
    hoodie: ['hoodie', 'sweatshirt'],
    sweatshirt: ['sweatshirt', 'hoodie'],
    jean: ['jean', 'denim'],
    denim: ['denim', 'jean'],
    jogger: ['jogger', 'sweatpant', 'tracksuit'],
    trackie: ['tracksuit', 'jogger'],
    silk: ['silk', 'satin'],
    satin: ['satin', 'silk'],
    leather: ['leather', 'suede'],
    wool: ['wool', 'cashmere', 'knit'],
    cashmere: ['cashmere', 'wool'],
    floral: ['floral', 'flower', 'print'],
    flower: ['floral', 'flower'],
    print: ['print', 'floral', 'leopard', 'plaid', 'stripe', 'striped', 'paisley'],
    stripe: ['stripe', 'striped'],
    plaid: ['plaid', 'check', 'checked', 'tartan'],
    check: ['check', 'checked', 'plaid'],
    grey: ['gray', 'grey', 'charcoal'],
    gray: ['gray', 'grey', 'charcoal'],
    burgundy: ['burgundy', 'wine', 'maroon', 'claret', 'bordeaux'],
    wine: ['wine', 'burgundy', 'maroon', 'claret'],
    maroon: ['maroon', 'burgundy', 'wine'],
    beige: ['beige', 'khaki', 'camel', 'sand', 'tan', 'nude', 'apricot', 'taupe'],
    nude: ['nude', 'beige', 'champagne'],
    camel: ['camel', 'beige', 'tan'],
    cream: ['cream', 'ivory', 'offwhite'],
    ivory: ['ivory', 'cream', 'offwhite'],
    navy: ['navy', 'midnight'],
    long: ['maxi', 'long'],
    wedding: ['gown', 'maxi', 'satin', 'lace', 'tuxedo', 'suit', 'blazer'],
    party: ['mini', 'satin', 'sequin', 'sequined', 'heel', 'gown'],
    evening: ['gown', 'maxi', 'satin', 'evening'],
    prom: ['gown', 'maxi'],
    cocktail: ['mini', 'midi', 'cocktail'],
    formal: ['suit', 'tuxedo', 'blazer', 'gown', 'oxford'],
    office: ['blazer', 'trouser', 'shirt', 'blouse', 'loafer', 'suit'],
    work: ['blazer', 'trouser', 'shirt', 'blouse', 'loafer', 'suit'],
    beach: ['linen', 'sundress', 'sandal', 'short', 'kaftan'],
    vacation: ['linen', 'sundress', 'sandal', 'short', 'maxi', 'kaftan'],
    holiday: ['linen', 'sundress', 'sandal', 'short', 'maxi', 'kaftan'],
    summer: ['linen', 'sundress', 'sandal', 'short', 'sleeveless'],
    winter: ['coat', 'wool', 'knit', 'boot', 'sweater', 'puffer', 'overcoat'],
    warm: ['coat', 'wool', 'knit', 'sweater', 'fleece', 'puffer'],
    comfy: ['orthopedic', 'knit', 'jogger', 'relaxed'],
    comfortable: ['orthopedic', 'knit', 'jogger', 'relaxed'],
    orthopaedic: ['orthopedic'],
  };
  const GENDER = {
    w: ['women', 'womens', 'woman', 'womans', 'ladies', 'lady', 'female', 'her', 'girl', 'girls', 'womenswear'],
    m: ['men', 'mens', 'man', 'mans', 'gents', 'gent', 'gentleman', 'gentlemen', 'male', 'him', 'guy', 'guys', 'boy', 'boys', 'menswear'],
  };
  const GWORD = {};
  for (const g of Object.keys(GENDER)) for (const w of GENDER[g]) GWORD[w] = g;
  const STOP = new Set(['a', 'an', 'the', 'for', 'and', 'or', 'with', 'in', 'on', 'of', 'to', 'my', 'me', 'size', 'sizes', 'color', 'colour', 'colors', 'colours', 'item', 'items', 'piece', 'pieces', 'clothes', 'clothing', 'outfits', 'style', 'styles', 'new', 'cheap', 'nice', 'best', 'buy', 'shop', 'sale', 'want', 'looking', 'something', 'some', 'any', 'unisex']);
  const SYN_STEM = {};
  for (const k of Object.keys(SYN)) if (!SYN_STEM[stem(k)]) SYN_STEM[stem(k)] = SYN[k];
  const synOf = (w) => SYN[w] || SYN_STEM[stem(w)] || null;

  /* Shopping words. Typo fixes and "still typing" completion only ever land on these, on the
     category rule words, product types and color names, never on a title's place name
     (cape is not Calpe, pajamas is not Palamos). */
  const MATERIALS = 'linen cotton silk satin wool cashmere leather suede denim lace velvet knit knitted chiffon tweed corduroy sequin sequined faux crochet jersey mesh fleece ribbed waffle cable chunky organza tulle crepe canvas mohair alpaca merino shearling sherpa nylon rattan straw';
  const STYLE_WORDS = 'mini midi maxi floral print printed wrap slip bodycon pleated ruffle ruffled tiered sleeveless strapless halter shoulder sleeve sleeves puff button buttoned cargo wide leg high waist waisted tailored oversized relaxed slim fitted cropped platform pointed block orthopedic casual classic vintage boho embroidered striped plaid checked leopard polka belted hooded zip collar collared neck square sweetheart cowl backless lined double breasted quilted padded long short ankle knee chelsea combat cowboy strappy kitten wedge toe bow';
  /* Clothing words for things the shop does not carry today. A correctly spelled word is never
     turned into a different one (robe is not rose, wallet is not ballet); it gets the honest
     "We could not find" answer, pointing to the closest category when there is one. */
  const NO_STOCK = {
    robe: '', bathrobe: '', pajama: '', pajamas: '', pyjama: '', pyjamas: '', pj: '', pjs: '', nightgown: '', nightie: '',
    bra: '', bralette: '', lingerie: '', underwear: '', panties: '', brief: '', briefs: '', boxer: '', boxers: '',
    swimsuit: '', bikini: '', swimwear: '', tankini: '', stocking: '', stockings: '',
    sock: 'accessories', tie: 'accessories', necktie: 'accessories', bowtie: 'accessories', watch: 'accessories',
    jewelry: 'accessories', jewellery: 'accessories', necklace: 'accessories', earring: 'accessories', bracelet: 'accessories',
    umbrella: 'accessories', shawl: 'accessories', cape: 'coats', poncho: 'coats', tights: 'bottoms', legging: 'bottoms', skirt: 'bottoms',
  };
  /* Occasion words: dresses and gowns come first (wedding guest is a dress search). */
  const OCCASION = new Set(['wedding', 'party', 'evening', 'prom', 'cocktail', 'gala', 'formal']);

  /* ---------- colors, sizes, lengths, prices ---------- */
  const FAMILIES = [
    ['black', 'Black', '#16181d'],
    ['white', 'White', '#ffffff'],
    ['beige', 'Beige', '#d9c7a7'],
    ['brown', 'Brown', '#6f4a2e'],
    ['gray', 'Gray', '#9a9a98'],
    ['navy', 'Navy', '#1e3a2c'],
    ['blue', 'Blue', '#5b84c4'],
    ['green', 'Green', '#5a7a4f'],
    ['red', 'Red', '#a3262a'],
    ['pink', 'Pink', '#e7a4b6'],
    ['purple', 'Purple', '#7e5aa2'],
    ['yellow', 'Yellow', '#e6c34a'],
    ['orange', 'Orange', '#d9772b'],
    ['metallic', 'Gold & Silver', 'linear-gradient(135deg,#c9a447,#f1e3b0 50%,#bfc2c7)'],
    ['multi', 'Print & Multi', 'conic-gradient(#a3262a,#e6c34a,#5a7a4f,#5b84c4,#7e5aa2,#a3262a)'],
  ];
  const FAM_NAME = Object.fromEntries(FAMILIES.map((f) => [f[0], f[1]]));
  const COLOR_WORD = {};
  const addColor = (fam, words) => words.split(' ').forEach((w) => (COLOR_WORD[w] = fam));
  addColor('black', 'black onyx jet obsidian stealth jaguar noir ebony');
  addColor('white', 'white ivory cream offwhite pure snow');
  addColor('beige', 'beige khaki apricot sand sandy sandstone camel tan champagne nude taupe desert natural oatmeal stone');
  addColor('brown', 'brown coffee chocolate mocha moka cognac chestnut espresso caramel darkbrown yellowish rust');
  addColor('gray', 'gray grey charcoal graphite silvery ash smoke');
  addColor('navy', 'navy marine midnight');
  addColor('blue', 'blue sky azure denim turquoise teal lake ocean aegean sapphire cobalt royal baby mist heaven');
  addColor('green', 'green olive army military mint sage emerald lime gren forest khakigreen');
  addColor('red', 'red burgundy wine claret bordeaux maroon cherry scarlet cardinal crimson rouge');
  addColor('pink', 'pink rose blush fuchsia fuschia salmon coral magenta');
  addColor('purple', 'purple lilac lavender lavander violet plum mauve');
  addColor('yellow', 'yellow mustard lemon butter');
  addColor('orange', 'orange terracotta peach');
  addColor('metallic', 'gold silver bronze metallic');
  const MULTI = /(&|\/|\band\b|multi|floral|flora|print|leopard|tiger|tortoise|marble|bloom|blossom|exotic|picture|havana|coastline|stripe|plaid)/i;
  const familyOf = (raw) => {
    let v = String(raw).replace(/\(.*?\)/g, ' ').trim();
    if (!v) return '';
    if (MULTI.test(v)) return 'multi';
    const words = norm(v).split(' ');
    if (words.includes('black')) return 'black';
    if (words.some((w) => w === 'navy' || w === 'marine' || w === 'midnight')) return 'navy';
    for (let i = words.length - 1; i >= 0; i--) if (COLOR_WORD[words[i]]) return COLOR_WORD[words[i]];
    return '';
  };
  const LETTER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const sizeOf = (raw) => {
    let s = String(raw).trim().toUpperCase().replace(/\.$/, '');
    if (s === 'XXL') s = '2XL';
    if (s === 'XXXL') s = '3XL';
    if (s === 'LARGE') s = 'L';
    if (s === 'MEDIUM') s = 'M';
    if (s === 'SMALL') s = 'S';
    if (s === 'ONE SIZE' || s === 'ONESIZE') s = 'One Size';
    if (LETTER.includes(s) || s === 'One Size' || /^\d+(\.5)?$/.test(s)) return s;
    return '';
  };
  const sizeRank = (s) => {
    const i = LETTER.indexOf(s);
    if (i >= 0) return i;
    if (s === 'One Size') return 999;
    return 100 + parseFloat(s);
  };
  const LENGTHS = [
    ['mini', 'Mini'],
    ['midi', 'Midi'],
    ['maxi', 'Maxi'],
  ];

  /* ---------- the index ---------- */
  let indexPromise = null;
  let INDEX = null;
  const previewParam = () => {
    const p = new URLSearchParams(location.search).get('preview_theme_id');
    if (p) return '&preview_theme_id=' + encodeURIComponent(p);
    /* Shopify drops preview_theme_id from the address after the first load; ask for the same theme anyway. */
    const t = window.Shopify && window.Shopify.theme;
    return t && t.id && t.role && t.role !== 'main' ? '&preview_theme_id=' + encodeURIComponent(t.id) : '';
  };
  /* In the theme editor (Customize) the page only shows the draft theme through the editor's own address
     keys (key, oseid, _fd, ...). The list request carries them over, or Shopify never answers it inside the
     editor and the search sat on "Searching..." (owner 2026-10-26). Outside the editor nothing changes. */
  const editorParams = () => {
    if (!(window.Shopify && window.Shopify.designMode)) return '';
    const out = new URLSearchParams();
    new URLSearchParams(location.search).forEach((v, k) => {
      if (!/^(q|page|sort_by|section_id|section_ids|view|variant|type|filter\..*|options\[.*\])$/.test(k)) out.set(k, v);
    });
    out.delete('preview_theme_id');
    const s = out.toString();
    return s ? '&' + s : '';
  };
  /* One page of the index. A request that hangs is dropped after 12 s; a busy (429), failing (5xx) or dropped
     answer is tried again up to 3 times, 1.5 s, 3 s, 4.5 s apart. A page that is not the list (not JSON)
     will not change on a retry, so it fails at once. */
  const fetchPage = async (n) => {
    const url = ROOT + 'collections/all?sort_by=best-selling&section_id=ba-find-index&page=' + n + previewParam() + editorParams();
    for (let a = 0; ; a++) {
      let status = 0;
      let text = null;
      const ac = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ac ? setTimeout(() => ac.abort(), 12000) : 0;
      try {
        const r = await fetch(url, { credentials: 'same-origin', signal: ac ? ac.signal : undefined });
        status = r.status;
        if (r.ok) text = await r.text();
      } catch (e) {
        status = 0; /* dropped or offline: worth another try */
      } finally {
        clearTimeout(timer);
      }
      if (text !== null) {
        /* the list starts at {"v": and ends at the first "]}" that closes it; the theme editor wraps the
           section in extra markup with braces of its own, so the first and last brace are not enough */
        const start = text.indexOf('{"v":');
        for (let end = text.indexOf(']}', start); start >= 0 && end > 0; end = text.indexOf(']}', end + 2)) {
          try {
            return JSON.parse(text.slice(start, end + 2));
          } catch (e) {}
        }
        throw new Error('index not JSON');
      }
      if (a >= 3 || (status !== 0 && status !== 429 && status < 500)) throw new Error('index ' + status);
      await new Promise((res) => setTimeout(res, 1500 * (a + 1)));
    }
  };

  const classifier = (rwStr, ruleStr) => {
    const rewrites = rwStr.split('|').map((s) => s.split('>'));
    const rules = ruleStr.split(';').map((seg) => {
      const i = seg.indexOf(':');
      return { key: seg.slice(0, i), words: seg.slice(i + 1).split(',') };
    });
    /* Same steps as the Liquid in sections/ba-category.liquid (and tools/categories-proof.js). */
    return (title) => {
      const tokens = title
        .toLowerCase()
        .replace(/’/g, "'")
        .replace(/‘/g, "'")
        .replace(/[(),]/g, ' ')
        .split(/[ \t\n\v\f\r]+/)
        .filter(Boolean);
      let t = ' ' + tokens.join(' ') + ' $' + (tokens[tokens.length - 1] || '') + ' ';
      for (const [from, to] of rewrites) {
        const f = ' ' + from + ' ';
        if (t.includes(f)) t = t.split(f).join(' ' + to + ' ');
      }
      for (const r of rules) for (const w of r.words) if (t.includes(w)) return r.key;
      return '';
    };
  };
  const CAT_WORDS = {
    dresses: ['dress'],
    tops: ['top'],
    bottoms: ['bottom'],
    suits: ['suit', 'set'],
    coats: ['coat', 'jacket'],
    shoes: ['shoe'],
    accessories: ['accessory'],
  };

  const build = (data) => {
    const classify = classifier(data.rw, data.rules);
    const titleVocab = new Set(); // every title word (for completion, minus place names)
    const firstW = new Set(); // first word of a title (the place name)
    const laterW = new Set(); // words seen anywhere else in a title
    const shop = new Set(); // shopping words
    const addShop = (text) => {
      for (const w of norm(text).split(' ')) if (w && !GWORD[w] && !/^\d+$/.test(w)) shop.add(w);
    };
    /* category rule words (skirt, wallet, scarf...), joined forms too (co-ord > coord) */
    for (const seg of String(data.rules || '').split(';')) {
      const words = seg.slice(seg.indexOf(':') + 1).split(',');
      for (const raw of words) {
        const w = raw.trim().replace(/^\$/, '');
        if (!w) continue;
        addShop(w);
        if (w.includes('-')) shop.add(w.replace(/-/g, ''));
      }
    }
    for (const k of Object.keys(SYN)) {
      addShop(k);
      SYN[k].forEach(addShop);
    }
    addShop(MATERIALS);
    addShop(STYLE_WORDS);
    for (const f of FAMILIES) addShop(f[0] + ' ' + f[1]);
    addShop(Object.keys(COLOR_WORD).join(' '));
    const items = data.items.map((it, rank) => {
      const cat = classify(it.t);
      const g = it.g || '';
      const colors = it.o ? it.o.split('|') : [];
      addShop(it.y);
      addShop(colors.join(' '));
      norm(it.t)
        .split(' ')
        .filter((w) => w && !GWORD[w])
        .forEach((w, i) => (i === 0 ? firstW : laterW).add(w));
      const fam = new Set(colors.map(familyOf).filter(Boolean));
      const sizes = new Set((it.s ? it.s.split('|') : []).map(sizeOf).filter(Boolean));
      const tnorm = norm(it.t);
      const lenM = tnorm.match(/\b(mini|midi|maxi)\b/);
      const titleWords = new Set();
      const otherWords = new Set();
      const addTo = (set, text, title) => {
        for (const w of norm(text).split(' ')) {
          if (!w || GWORD[w]) continue;
          set.add(stem(w));
          if (title) titleVocab.add(w);
        }
      };
      addTo(titleWords, it.t, true);
      /* joined forms of hyphenated words: t-shirt -> tshirt, co-ord -> coord, slip-ons -> slipon */
      for (const h of it.t.toLowerCase().match(/[a-z0-9]+(?:-[a-z0-9]+)+/g) || []) {
        const j = h.replace(/-/g, '');
        titleWords.add(stem(j));
        titleVocab.add(j);
        laterW.add(j);
      }
      addTo(otherWords, it.y);
      addTo(otherWords, colors.join(' '));
      for (const f of fam) otherWords.add(stem(f));
      for (const w of CAT_WORDS[cat] || []) otherWords.add(stem(w));
      return {
        h: it.h,
        t: it.t,
        p: it.p,
        c: it.c,
        pm: it.pm,
        cm: it.cm,
        i: it.i,
        g,
        cat,
        fam,
        sizes,
        len: lenM ? lenM[1] : '',
        d: it.d,
        a: it.a,
        rank,
        tw: titleWords,
        rw: tnorm.split(' '),
        ox: norm([it.y, colors.join(' ')].join(' ')).split(' '),
        ow: otherWords,
      };
    });
    /* a place name = a word only ever seen as the first word of a title, and no shopping word */
    const place = (w) => firstW.has(w) && !laterW.has(w) && !shop.has(w);
    /* typo fixes: shopping words, the no-stock clothing words (so they match themselves and are
       never bent into a neighbor), and the women's / men's words */
    const vocab = new Map();
    for (const w of shop) if (!place(w)) vocab.set(w, stem(w));
    for (const w of Object.keys(NO_STOCK)) vocab.set(w, stem(w));
    for (const k of Object.keys(GWORD)) vocab.set(k, k);
    /* exact words a shopper may type that need no fixing */
    const words = new Set([...shop, ...Object.keys(NO_STOCK)]);
    /* still typing: shopping words plus title words, never a place name */
    const pre = new Set();
    for (const w of shop) if (!place(w)) pre.add(w);
    for (const w of titleVocab) if (!place(w)) pre.add(w);
    return { items, vocab, words, pre: [...pre], classify, cur: data.cur };
  };

  const loadIndex = (count) => {
    if (indexPromise) return indexPromise;
    indexPromise = (async () => {
      let data = null;
      /* this tab first, then an earlier visit on this device (localStorage), so a second visit answers at once */
      for (const store of ['sessionStorage', 'localStorage']) {
        try {
          const c = JSON.parse(window[store].getItem(CACHE_KEY) || 'null');
          if (c && Date.now() - c.at < TTL && c.items && c.items.length) {
            data = c;
            break;
          }
        } catch (e) {}
      }
      if (!data) {
        /* all pages at once: the index is always All products, whose size layout/theme.liquid puts on the
           ba-search.js tag (data-count), so no page waits for the one before it */
        const tag = document.querySelector('script[data-find][data-count]');
        const all = Math.max(count || 0, (tag && Number(tag.dataset.count)) || 0);
        const guess = Math.max(1, Math.ceil((all || 250) / 250));
        const first = await Promise.all(Array.from({ length: guess }, (_, i) => fetchPage(i + 1)));
        for (let i = guess + 1; i <= first[0].pages; i++) first.push(await fetchPage(i));
        const seen = new Set();
        const items = [];
        for (const r of first) for (const it of r.items) if (!seen.has(it.h)) (seen.add(it.h), items.push(it));
        data = { at: Date.now(), rw: first[0].rw, rules: first[0].rules, cur: first[0].cur, items };
        const raw = JSON.stringify(data);
        for (const store of ['sessionStorage', 'localStorage']) {
          try {
            window[store].setItem(CACHE_KEY, raw);
          } catch (e) {}
        }
      }
      INDEX = build(data);
      return INDEX;
    })();
    indexPromise.catch(() => {
      indexPromise = null;
    });
    return indexPromise;
  };

  /* ---------- search ---------- */
  const parseQuery = (q, index) => {
    let s = ' ' + norm(q) + ' ';
    for (const [re, rep] of PHRASES) s = s.replace(re, rep);
    const out = { groups: [], gender: '', maxPrice: 0, minPrice: 0, fixed: [], dropped: [], nostock: [] };
    const under = s.match(/\b(?:under|below|less than|max|up to)\s+(\d{2,4})\b/);
    if (under) {
      out.maxPrice = Number(under[1]) * 100;
      s = s.replace(under[0], ' ');
    }
    const over = s.match(/\b(?:over|above|more than|from)\s+(\d{2,4})\b/);
    if (over) {
      out.minPrice = Number(over[1]) * 100;
      s = s.replace(over[0], ' ');
    }
    const words = s.trim().split(/\s+/).filter(Boolean);
    words.forEach((w, idx) => {
      const last = idx === words.length - 1;
      if (GWORD[w]) {
        out.gender = GWORD[w];
        return;
      }
      if (STOP.has(w) || /^\d+$/.test(w) || w.length < 2) return;
      let terms = new Set([stem(w)]);
      const syn = synOf(w);
      if (syn) syn.forEach((x) => terms.add(stem(x)));
      const has = (set) => index.items.some((p) => [...set].some((t) => p.tw.has(t) || p.ow.has(t)));
      let known = has(terms);
      let fixed = '';
      if (!known) {
        const base = [w, w.replace(/s$/, ''), w.replace(/es$/, '')].find((x) => x.length >= 2 && index.words.has(x));
        const bt = new Set(base ? [stem(base)] : []);
        if (base) (synOf(base) || []).forEach((x) => bt.add(stem(x)));
        if (base && base !== w && has(bt)) {
          /* an extra s on a word we carry (dresss): that word */
          terms = bt;
          known = true;
          fixed = base;
          out.fixed.push([w, base]);
        } else if (base) {
          /* a real word with no piece behind it (skirt, robe, wallet): say so, never bend it */
          const cat = base in NO_STOCK ? NO_STOCK[base] : index.classify(base) || index.classify(base + 's');
          out.nostock.push([w, cat || '']);
          out.dropped.push(w);
          return;
        }
      }
      let prefix = '';
      if (!known && last && w.length >= 3) {
        /* still typing: dre -> dress */
        const done = index.pre.filter((v) => v.startsWith(w) && !GWORD[v]);
        if (done.length) {
          prefix = w;
          known = true;
          for (const v of done.slice(0, 12)) {
            terms.add(stem(v));
            const s3 = synOf(v);
            if (s3 && done.length <= 3) s3.forEach((x) => terms.add(stem(x)));
          }
        }
      }
      if (!known && w.length >= 4) {
        const max = w.length >= 7 ? 2 : 1;
        let best = max + 1;
        let bestWords = [];
        for (const v of index.vocab.keys()) {
          if (v.length < 3) continue;
          /* typos keep the first letter (or swap the first two): pockets is not jackets */
          if (v[0] !== w[0] && !(v[0] === w[1] && v[1] === w[0])) continue;
          const dd = dist(w, v, max);
          if (dd < best) {
            best = dd;
            bestWords = [v];
          } else if (dd === best) bestWords.push(v);
        }
        if (best <= max) {
          /* a typo close to a product word AND to a gender word ("bots": boots or boys) is the product word;
             it only turns into Women's or Men's when nothing else is that close (final check 2026-10-26) */
          if (bestWords.some((v) => !GWORD[v])) bestWords = bestWords.filter((v) => !GWORD[v]);
          terms = new Set();
          for (const v of bestWords) {
            if (GWORD[v]) {
              out.gender = GWORD[v];
              continue;
            }
            terms.add(stem(v));
            const s2 = synOf(v);
            if (s2) s2.forEach((x) => terms.add(stem(x)));
          }
          if (!terms.size) return;
          fixed = bestWords.filter((v) => !GWORD[v])[0] || '';
          if (fixed) out.fixed.push([w, fixed]);
          known = true;
        }
      }
      if (!known) {
        out.dropped.push(w);
        return;
      }
      out.groups.push({ word: w, own: stem(fixed || w), terms, prefix, fuzzy: !!fixed });
    });
    return out;
  };
  /* Returns a Map handle -> score for the pieces that answer the query (all words), plus
     closest matches (most words) when no piece answers all of them. */
  const runSearch = (q, index) => {
    const pq = parseQuery(q, index);
    /* strong: every word found as the shopper wrote it (title, type, color or category), not
       only through an everyday-word match (gown > dress) */
    const res = { scores: new Map(), strong: new Set(), partial: false, pq };
    if (!pq.groups.length) return res;
    const partial = new Map();
    for (const p of index.items) {
      let score = 0;
      let hits = 0;
      let own = true;
      for (const gr of pq.groups) {
        if (!(p.tw.has(gr.own) || p.ow.has(gr.own))) own = false;
        /* title words count most (the shopper's own word 4, an everyday-word match 3); a hit
           in the type, color or category adds 1, so a dress outranks a dress shirt for "dress" */
        let s = 0;
        if (p.tw.has(gr.own)) s = 4;
        else {
          for (const t of gr.terms) {
            if (p.tw.has(t)) {
              s = 3;
              break;
            }
          }
        }
        let other = false;
        for (const t of gr.terms) {
          if (p.ow.has(t)) {
            other = true;
            break;
          }
        }
        if (gr.prefix) {
          if (!s && p.rw.some((x) => x.startsWith(gr.prefix))) s = 2;
          if (!other && p.ox.some((x) => x.startsWith(gr.prefix))) other = true;
        }
        s = s ? s + (other ? 1 : 0) : other ? 1.5 : 0;
        if (gr.fuzzy) s *= 0.9;
        if (s && OCCASION.has(gr.word) && p.cat === 'dresses') s += 2;
        if (s) hits++;
        score += s;
      }
      if (hits === pq.groups.length) {
        res.scores.set(p.h, score);
        if (own) res.strong.add(p.h);
      }
      else if (hits) partial.set(p.h, score + hits * 10);
    }
    if (!res.scores.size && partial.size) {
      res.scores = partial;
      res.partial = true;
    }
    return res;
  };

  /* ---------- the page ---------- */
  const PRICE_US = [
    ['u50', 'Under $50', 0, 5000],
    ['50-75', '$50 to $75', 5000, 7500],
    ['75-100', '$75 to $100', 7500, 10000],
    ['100up', '$100 and up', 10000, Infinity],
  ];
  const SORTS = {
    best: 'Best sellers',
    featured: 'Featured',
    new: 'Newest',
    'price-asc': 'Price, low to high',
    'price-desc': 'Price, high to low',
  };

  class Finder {
    constructor(el) {
      this.el = el;
      this.cfg = JSON.parse(el.querySelector('[data-ba-find-config]').textContent);
      this.extraW = new Set(this.cfg.extraW || []);
      this.input = el.querySelector('[data-ba-q]');
      this.form = el.querySelector('[data-ba-form]');
      this.clearBtn = el.querySelector('[data-ba-clear-q]');
      this.filterBtn = el.querySelector('[data-ba-open]');
      this.fcount = el.querySelector('[data-ba-fcount]');
      this.sortSel = el.querySelector('[data-ba-sort]');
      this.sortLabel = el.querySelector('[data-ba-sort-label]');
      this.summary = el.querySelector('[data-ba-summary]');
      this.activeRow = el.querySelector('[data-ba-active]');
      this.results = document.querySelector('[data-ba-results]');
      this.grid = this.results.querySelector('[data-ba-grid]');
      this.note = this.results.querySelector('[data-ba-note]');
      this.moreBtn = this.results.querySelector('[data-ba-more]');
      this.moreText = this.results.querySelector('[data-ba-more-text]');
      this.sheet = el.querySelector('[data-ba-sheet]');
      this.sheetBody = this.sheet.querySelector('[data-ba-sheet-body]');
      this.showBtn = this.sheet.querySelector('[data-ba-show]');
      this.defaultGrid = this.findDefaultGrid();
      this.scope = { g: this.cfg.g || '', c: this.cfg.c || '', sort: this.cfg.sort || 'best' };
      this.state = this.fromHash();
      this.finding = false;
      this.auto = 0;
      this.bind();
      const startQ = new URLSearchParams(location.search).get('q');
      if (startQ && !this.state.q) {
        this.state.q = startQ;
        const u = new URL(location.href);
        u.searchParams.delete('q');
        u.searchParams.delete('type');
        u.searchParams.delete('options[prefix]');
        history.replaceState(history.state, '', u.pathname + u.search + u.hash);
      }
      this.input.value = this.state.q;
      this.syncControls();
      if (!this.isDefault()) {
        this.load().then(() => this.apply({ restore: true }));
      } else {
        const go = () => this.load().then(() => this.updateSummary());
        if (document.readyState === 'complete') this.idle(go);
        else window.addEventListener('load', () => this.idle(go), { once: true });
      }
      if (/(^|[#&])focus=1/.test(location.hash) || this.cfg.focus) {
        try {
          this.input.focus({ preventScroll: true });
        } catch (e) {}
      }
    }
    idle(fn) {
      if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 });
      else setTimeout(fn, 1200);
    }
    findDefaultGrid() {
      const wrap = document.querySelector('.ba-cat__wrap');
      if (wrap) return wrap;
      const grid = document.querySelector('.main-collection-grid');
      return grid ? grid.closest('.shopify-section') : null;
    }
    load() {
      this.el.classList.add('is-loading');
      if (!this.ix && this.summary && ((this.input && this.input.value) || '').trim()) this.summary.textContent = 'Searching...';
      return loadIndex(this.cfg.count, this.extraW)
        .then((ix) => {
          this.ix = ix;
          this.el.classList.remove('is-loading');
          return ix;
        })
        .catch(() => {
          this.el.classList.remove('is-loading');
          this.el.classList.add('is-offline');
          /* Never a dead search: after every retry failed, a typed search goes to Shopify's own search page
             (ba=0 tells layout/theme.liquid not to send it back here). */
          const q = ((this.input && this.input.value) || '').trim();
          if (q && !(window.Shopify && window.Shopify.designMode)) {
            location.href = ROOT + 'search?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last&ba=0';
          } else if (q && this.summary) {
            /* in the theme editor the page stays put; say so instead of "Searching..." for ever */
            this.summary.textContent = 'Search could not load here. Try the preview link.';
          }
          throw new Error('index');
        });
    }

    /* state <-> address */
    blank() {
      return { q: '', g: this.scope.g, c: this.scope.c, col: [], size: [], price: [], len: [], sort: this.scope.sort, n: STEP };
    }
    fromHash() {
      const st = this.blank();
      const h = new URLSearchParams(location.hash.replace(/^#/, ''));
      if (h.has('q')) st.q = h.get('q');
      if (h.has('g')) st.g = h.get('g') === 'any' ? '' : h.get('g');
      if (h.has('c')) st.c = h.get('c') === 'any' ? '' : h.get('c');
      for (const k of ['col', 'size', 'price', 'len']) if (h.get(k)) st[k] = h.get(k).split(',');
      if (h.has('sort') && SORTS[h.get('sort')]) st.sort = h.get('sort');
      if (h.has('n')) st.n = Math.max(STEP, Math.min(600, Number(h.get('n')) || STEP));
      return st;
    }
    toHash() {
      const st = this.state;
      const h = new URLSearchParams();
      if (st.q) h.set('q', st.q);
      if (st.g !== this.scope.g) h.set('g', st.g || 'any');
      if (st.c !== this.scope.c) h.set('c', st.c || 'any');
      for (const k of ['col', 'size', 'price', 'len']) if (st[k].length) h.set(k, st[k].join(','));
      if (st.sort !== this.scope.sort) h.set('sort', st.sort);
      if (st.n > STEP) h.set('n', String(st.n));
      const s = h.toString();
      return s ? '#' + s : '';
    }
    writeHash() {
      const url = location.pathname + location.search + this.toHash();
      if (url !== location.pathname + location.search + location.hash) {
        try {
          history.replaceState(history.state, '', url);
        } catch (e) {}
      }
    }
    isDefault() {
      const st = this.state;
      return (
        !st.q.trim() &&
        st.g === this.scope.g &&
        st.c === this.scope.c &&
        !st.col.length &&
        !st.size.length &&
        !st.price.length &&
        !st.len.length &&
        st.sort === this.scope.sort
      );
    }

    bind() {
      let timer = 0;
      this.input.addEventListener('input', () => {
        this.state.q = this.input.value;
        this.state.n = STEP;
        this.clearBtn.hidden = !this.input.value;
        clearTimeout(timer);
        timer = setTimeout(() => this.load().then(() => this.apply(), () => {}), 160);
      });
      this.input.addEventListener('focus', () => this.load().catch(() => {}), { once: true });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.state.q = this.input.value;
        this.state.n = STEP;
        this.input.blur();
        this.load().then(() => {
          this.apply();
          this.scrollToResults();
        }).catch(() => {}); /* load() itself hands a typed search on when the list cannot load */
      });
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.state.q = '';
        this.state.n = STEP;
        this.clearBtn.hidden = true;
        this.input.focus();
        this.load().then(() => this.apply());
      });
      this.filterBtn.addEventListener('click', () => this.openSheet());
      this.sortSel.addEventListener('change', () => {
        this.state.sort = this.sortSel.value;
        this.state.n = STEP;
        this.load().then(() => this.apply());
      });
      this.sheet.addEventListener('click', (e) => {
        if (e.target === this.sheet) this.closeSheet();
        const b = e.target.closest('[data-k]');
        if (b) this.toggle(b.dataset.k, b.dataset.v);
        if (e.target.closest('[data-ba-close]') || e.target.closest('[data-ba-show]')) this.closeSheet();
        if (e.target.closest('[data-ba-reset]')) this.reset(true);
      });
      this.sheet.addEventListener('close', () => document.documentElement.classList.remove('ba-find-lock'));
      const onRemove = (e) => {
        const b = e.target.closest('[data-rm]');
        if (!b) return;
        const [k, v] = b.dataset.rm.split(':');
        if (k === 'all') this.reset(false);
        else if (k === 'q') {
          this.state.q = '';
          this.state.n = STEP;
          this.input.value = '';
          this.apply();
        } else this.toggle(k, v);
      };
      this.el.addEventListener('click', onRemove);
      this.results.addEventListener('click', onRemove);
      this.results.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-h]');
        if (a) this.remember();
        const t = e.target.closest('[data-try]');
        if (t) {
          e.preventDefault();
          this.input.value = t.dataset.try;
          this.state = Object.assign(this.blank(), { q: t.dataset.try });
          this.apply();
        }
        if (e.target.closest('[data-ba-relax]')) {
          const q = this.state.q;
          this.state = Object.assign(this.blank(), { q, g: '', c: '' });
          this.apply();
        }
      });
      this.moreBtn.addEventListener('click', () => this.more());
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (en) => {
            if (en.some((x) => x.isIntersecting) && this.finding && this.auto < 3 && !this.moreBtn.hidden) {
              this.auto += 1;
              this.more();
            }
          },
          { rootMargin: '600px 0px' }
        );
        this.io.observe(this.moreBtn);
      }
      window.addEventListener('pagehide', () => this.remember());
      /* Coming back from a product page out of the browser's memory: nothing to do, the
         page is exactly as it was. */
    }
    remember() {
      if (!this.finding) return;
      try {
        sessionStorage.setItem('ba-find:y:' + location.pathname + location.search + location.hash, String(Math.round(scrollY)));
      } catch (e) {}
    }
    scrollToResults() {
      const top = this.el.getBoundingClientRect().top + scrollY - 70;
      if (scrollY > top + 40) window.scrollTo({ top, behavior: 'smooth' });
    }
    reset(keepSheet) {
      const q = keepSheet ? this.state.q : '';
      this.state = Object.assign(this.blank(), { q });
      if (!keepSheet) {
        this.input.value = '';
        this.clearBtn.hidden = true;
      }
      this.apply();
    }
    toggle(k, v) {
      const st = this.state;
      st.n = STEP;
      if (k === 'g' || k === 'c') {
        st[k] = st[k] === v ? '' : v;
        if (k === 'g' && st.c && !this.catsFor(st.g).includes(st.c)) st.c = '';
      } else {
        const a = st[k];
        const i = a.indexOf(v);
        if (i >= 0) a.splice(i, 1);
        else a.push(v);
      }
      this.apply();
    }
    more() {
      this.state.n += STEP;
      this.renderGrid(false);
      this.writeHash();
    }

    /* filtering */
    catsFor(g) {
      return g === 'm' ? ['tops', 'bottoms', 'suits', 'coats', 'shoes', 'accessories'] : ['dresses', 'tops', 'bottoms', 'suits', 'coats', 'shoes', 'accessories'];
    }
    catName(c, g) {
      const L = this.cfg.labels || {};
      const set = L[g === 'm' ? 'm' : g === 'w' ? 'w' : 'a'] || {};
      return set[c] || c;
    }
    priceBuckets() {
      if (this._pb) return this._pb;
      if (!this.ix || this.ix.cur === 'USD') return (this._pb = PRICE_US);
      /* other currencies: four even groups by price, rounded */
      const ps = this.ix.items.map((p) => p.p).sort((a, b) => a - b);
      const at = (f) => Math.round(ps[Math.floor(f * (ps.length - 1))] / 1000) * 1000;
      const a = at(0.2);
      const b = at(0.5);
      const c = at(0.8);
      const fmt = (x) => {
        const m = (this.ix.items[0].pm || '').match(/^[^\d]*/);
        return (m ? m[0] : '') + Math.round(x / 100);
      };
      return (this._pb = [
        ['u50', 'Under ' + fmt(a), 0, a],
        ['50-75', fmt(a) + ' to ' + fmt(b), a, b],
        ['75-100', fmt(b) + ' to ' + fmt(c), b, c],
        ['100up', fmt(c) + ' and up', c, Infinity],
      ]);
    }
    /* Is p in gender g? The few women's pieces that sit in no Women's or Men's collection
       (cfg.extraW) count as women's only where the page grid shows them too: inside their own
       category (Women's Shoes), so Women's itself says the same number everywhere. */
    gHas(p, g, c) {
      if (p.g.includes(g)) return true;
      return g === 'w' && !p.g && !!c && c === p.cat && this.extraW.has(p.h);
    }
    match(p, st, skip) {
      if (skip !== 'g' && st.g && !this.gHas(p, st.g, skip === 'c' ? p.cat : st.c)) return false;
      if (skip !== 'c' && st.c && p.cat !== st.c) return false;
      if (skip !== 'len' && st.len.length && !st.len.includes(p.len)) return false;
      if (skip !== 'col' && st.col.length && !st.col.some((c) => p.fam.has(c))) return false;
      if (skip !== 'size' && st.size.length && !st.size.some((s) => p.sizes.has(s))) return false;
      if (skip !== 'price' && st.price.length) {
        const pb = this.priceBuckets();
        if (!st.price.some((k) => pb.some((b) => b[0] === k && p.p >= b[2] && p.p < b[3]))) return false;
      }
      return true;
    }
    queryPool(st) {
      /* items answering the search (all of them when there is no search) */
      const q = st.q.trim();
      if (!q) return { pool: this.ix.items, search: null };
      const s = runSearch(q, this.ix);
      const pq = s.pq;
      /* only filler words (new, size, the): treat as no search */
      if (!pq.groups.length && !pq.dropped.length && !pq.gender && !pq.maxPrice && !pq.minPrice) return { pool: this.ix.items, search: null };
      let pool = this.ix.items.filter((p) => s.scores.has(p.h));
      if (pq.gender) pool = pool.filter((p) => this.gHas(p, pq.gender, st.c || p.cat));
      if (pq.maxPrice) pool = pool.filter((p) => p.p <= pq.maxPrice);
      if (pq.minPrice) pool = pool.filter((p) => p.p >= pq.minPrice);
      if (!pq.groups.length && !pq.nostock.length && (pq.gender || pq.maxPrice || pq.minPrice)) {
        pool = this.ix.items.filter(
          (p) => (!pq.gender || this.gHas(p, pq.gender, st.c)) && (!pq.maxPrice || p.p <= pq.maxPrice) && (!pq.minPrice || p.p >= pq.minPrice)
        );
      }
      return { pool, search: s };
    }
    order(list, st, search) {
      const by = st.sort;
      const sc = search && search.scores;
      return list.slice().sort((a, b) => {
        if (by === 'new') return b.d - a.d || a.rank - b.rank;
        if (by === 'price-asc') return a.p - b.p || a.rank - b.rank;
        if (by === 'price-desc') return b.p - a.p || a.rank - b.rank;
        if (sc && sc.size) return (sc.get(b.h) || 0) - (sc.get(a.h) || 0) || a.rank - b.rank;
        return a.rank - b.rank;
      });
    }
    compute() {
      const st = this.state;
      const { pool, search } = this.queryPool(st);
      let list = pool.filter((p) => this.match(p, st));
      let note = '';
      let noteHtml = '';
      const q = st.q.trim();
      const qShown = '“' + esc(q) + '”';
      const pq = search && search.pq;
      const gl = st.g === 'w' ? "Women's" : st.g === 'm' ? "Men's" : '';
      /* wider scopes to try: this gender in every category, then the whole shop */
      const wider = (ok) => {
        const tries = [];
        if (st.c && st.g) tries.push({ c: '' });
        if (st.c || st.g) tries.push({ g: '', c: '' });
        for (const t of tries) {
          const w = pool.filter((p) => this.match(p, Object.assign({}, st, t)));
          if (ok(w)) return { list: w, where: t.g === '' || !gl ? 'the whole shop' : 'all of ' + gl };
        }
        return null;
      };
      if (search) {
        if (pq.fixed.length) {
          noteHtml = 'Showing results for “' + esc(pq.fixed.map((f) => f[1]).join(' ')) + '”.';
        }
        if (search.partial && list.length) {
          noteHtml = 'No piece matches every word of ' + qShown + ', so here are the closest.';
        }
        /* The page's own category only has everyday-word matches (gown on Men's Shoes finds a
           dress shoe) while the shop has the real thing: show it from the whole shop. */
        const own = st.g === this.scope.g && st.c === this.scope.c && (st.g || st.c);
        if (own && list.length && !search.partial && !list.some((p) => search.strong.has(p.h))) {
          const w = wider((l) => l.some((p) => search.strong.has(p.h)));
          if (w) {
            list = w.list;
            noteHtml = 'Showing ' + qShown + ' from ' + w.where + '.';
          }
        }
        if (pq.nostock.length && list.length && pq.groups.length) {
          noteHtml =
            'We could not find “' + esc(pq.nostock.map((x) => x[0]).join(' ')) + '”, so here are pieces matching “' +
            esc(pq.groups.map((x) => (pq.fixed.find((f) => f[0] === x.word) || [0, x.word])[1]).join(' ')) + '”.' +
            this.pointer(pq, 'link');
        }
      }
      if (!list.length && search && pool.length) {
        /* the search found pieces, but not inside these filters: show them across the shop */
        const w = wider((l) => l.length > 0);
        list = w ? w.list : pool;
        noteHtml =
          'Nothing here matches ' + qShown + ' with these filters, so here it is across ' + (w ? w.where : 'the whole shop') + '. ' +
          '<button type="button" class="ba-find__link" data-rm="all:">Clear filters</button>';
      }
      if (!list.length && !search) {
        list = this.ix.items.filter((p) => this.match(p, this.blank()));
        if (!list.length) list = this.ix.items.slice();
        noteHtml =
          'No piece matches all of these filters yet. Here is everything else in this edit. ' +
          '<button type="button" class="ba-find__link" data-rm="all:">Clear filters</button>';
      }
      if (!list.length) {
        const sc = this.blank();
        list = this.ix.items.filter((p) => this.match(p, sc));
        if (!list.length) list = this.ix.items.slice();
        const tries = (this.cfg.popular || []).map(
          (t) => '<button type="button" class="ba-find__try" data-try="' + esc(t) + '">' + esc(t) + '</button>'
        );
        const go = pq ? this.pointer(pq, 'chip') : '';
        const missing = pq && pq.nostock.length ? '“' + esc(pq.nostock.map((x) => x[0]).join(' ')) + '”' : qShown;
        noteHtml =
          'We could not find ' + missing + '. ' + (go ? 'Try the closest category or one of these' : 'Try one of these') +
          ', or browse our best sellers below.' +
          (tries.length || go ? '<span class="ba-find__tries">' + go + tries.join('') + '</span>' : '');
        search && (search.scores = new Map());
      }
      return { list: this.order(list, st, search), noteHtml, note };
    }
    /* A link to the category closest to a word we do not carry (skirt > Bottoms). */
    pointer(pq, kind) {
      const hit = pq.nostock.find((x) => x[1]);
      if (!hit) return '';
      const c = hit[1];
      const g = this.state.g || pq.gender || 'w';
      const hand = c === 'accessories' ? 'all' : g === 'm' && c !== 'dresses' ? 'mens' : 'womens';
      const name = this.catName(c, c === 'accessories' ? '' : hand === 'mens' ? 'm' : 'w');
      const url = ROOT + 'collections/' + hand + '?view=' + encodeURIComponent(c);
      return kind === 'chip'
        ? '<a class="ba-find__try ba-find__try--go" href="' + esc(url) + '">' + esc(name) + '</a>'
        : ' <a class="ba-find__link" href="' + esc(url) + '">Browse ' + esc(name) + '</a>';
    }
    facets() {
      const st = this.state;
      const { pool } = this.queryPool(st);
      const count = (skip, test) => pool.filter((p) => this.match(p, st, skip) && test(p)).length;
      const out = {};
      out.g = [
        ['w', "Women's"],
        ['m', "Men's"],
      ].map(([v, l]) => [v, l, count('g', (p) => this.gHas(p, v, st.c))]);
      out.c = this.catsFor(st.g).map((v) => [v, this.catName(v, st.g), count('c', (p) => p.cat === v)]);
      out.len = LENGTHS.map(([v, l]) => [v, l, count('len', (p) => p.len === v)]);
      out.col = FAMILIES.map(([v, l, sw]) => [v, l, count('col', (p) => p.fam.has(v)), sw]);
      const sizes = new Set();
      for (const p of pool) if (this.match(p, st, 'size')) for (const s of p.sizes) sizes.add(s);
      for (const s of st.size) sizes.add(s);
      out.size = [...sizes].sort((a, b) => sizeRank(a) - sizeRank(b)).map((v) => [v, v, count('size', (p) => p.sizes.has(v))]);
      out.price = this.priceBuckets().map(([v, l]) => [v, l, count('price', (p) => this.priceBuckets().some((b) => b[0] === v && p.p >= b[2] && p.p < b[3]))]);
      return out;
    }

    /* drawing */
    apply(opts) {
      if (!this.ix) return;
      const restore = opts && opts.restore;
      const def = this.isDefault();
      this.finding = !def;
      this.el.classList.toggle('is-finding', !def);
      document.documentElement.classList.toggle('ba-is-finding', !def);
      if (this.defaultGrid) {
        /* The attribute carries a display: none !important rule (snippets/ba-find.liquid): the
           category grid's own display: flex beats a plain hidden attribute. */
        this.defaultGrid.hidden = !def;
        this.defaultGrid.toggleAttribute('data-ba-find-hidden', !def);
      }
      this.results.hidden = def;
      this.auto = 0;
      if (def) {
        this.grid.innerHTML = '';
        this.note.innerHTML = '';
        this.moreBtn.hidden = true;
        this.lastList = null;
      } else {
        this.renderGrid(true, restore);
      }
      this.syncControls();
      this.updateSummary();
      this.heading();
      if (this.sheet.open) this.renderSheet();
      this.writeHash();
      if (restore) {
        let back = false;
        try {
          const nav = performance.getEntriesByType('navigation')[0];
          back = !!nav && (nav.type === 'back_forward' || nav.type === 'reload');
        } catch (e) {}
        let y = 0;
        try {
          y = Number(sessionStorage.getItem('ba-find:y:' + location.pathname + location.search + location.hash)) || 0;
        } catch (e) {}
        if (back && y) {
          window.scrollTo(0, y);
          requestAnimationFrame(() => window.scrollTo(0, y));
        }
      }
    }
    /* When the shopper switches Women's / Men's or the category inside the filters, the page
       title follows, so the heading always says what is shown. */
    /* The category chip row follows the filter: the navy chip is the category shown. */
    chips() {
      const st = this.state;
      const sc = this.scope;
      const own = st.g === sc.g && st.c === sc.c;
      /* chip page -> what it shows [g, c]; the finer pages (Knitwear, Tops & Blouses, Shirts & Polos,
         Knitwear & Hoodies) have no exact filter match, so they only light up on their own page */
      const MAP = {
        all: ['', ''], womens: ['w', ''], mens: ['m', ''], accessories: ['', 'accessories'],
        'womens-dresses': ['w', 'dresses'], 'womens-trousers-skirts': ['w', 'bottoms'], 'womens-sets-jumpsuits': ['w', 'suits'],
        'womens-coats-jackets': ['w', 'coats'], 'womens-shoes-boots': ['w', 'shoes'],
        'mens-trousers-jeans-shorts': ['m', 'bottoms'], 'mens-suits-blazers': ['m', 'suits'],
        'mens-coats-jackets': ['m', 'coats'], 'mens-shoes-boots': ['m', 'shoes']
      };
      document.querySelectorAll('.ba-cat__chips .ba-cats__chip').forEach((a) => {
        if (a.dataset.own === undefined) a.dataset.own = a.classList.contains('is-active') ? '1' : '';
        let u;
        try {
          u = new URL(a.getAttribute('href'), location.href);
        } catch (e) {
          return;
        }
        const m = MAP[u.pathname.replace(/\/$/, '').split('/').pop()];
        const on = own ? a.dataset.own === '1' : !!m && m[1] === st.c && (m[1] === 'accessories' || m[0] === st.g);
        const was = a.classList.contains('is-active');
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', own ? 'page' : 'true');
        else a.removeAttribute('aria-current');
        if (on && !was) {
          const row = a.closest('.ba-cats__row');
          if (row) {
            const r = row.getBoundingClientRect();
            const c = a.getBoundingClientRect();
            if (c.right > r.right || c.left < r.left) row.scrollLeft += c.left - r.left - 24;
          }
        }
      });
    }
    heading() {
      this.chips();
      const t = document.querySelector('.ba-cat__title');
      if (!t) return;
      const o = document.querySelector('.ba-cat__over');
      const l = document.querySelector('.ba-cat__line');
      if (!this.h0) this.h0 = { t: t.textContent, o: o ? o.textContent : '', oh: o ? o.hidden : true, l: l ? l.hidden : true };
      const st = this.state;
      if (st.g === this.scope.g && st.c === this.scope.c) {
        t.textContent = this.h0.t;
        if (o) (o.textContent = this.h0.o), (o.hidden = this.h0.oh);
        if (l) l.hidden = this.h0.l;
        return;
      }
      const gl = st.g === 'w' ? "Women's" : st.g === 'm' ? "Men's" : '';
      const name = st.c ? this.catName(st.c, st.g) : gl || 'All products';
      if (o) {
        o.textContent = st.c && st.c !== 'accessories' ? gl : '';
        o.hidden = !o.textContent;
        t.textContent = name;
      } else t.textContent = st.c && gl && st.c !== 'accessories' ? gl + ' ' + name : name;
      if (l) l.hidden = true;
    }
    renderGrid(fresh, eager) {
      if (fresh || !this.lastList) {
        const r = this.compute();
        this.lastList = r.list;
        this.note.innerHTML = r.noteHtml;
        this.note.hidden = !r.noteHtml;
        this.grid.innerHTML = '';
        this.drawn = 0;
      }
      const list = this.lastList;
      const upto = Math.min(this.state.n, list.length);
      let html = '';
      for (let i = this.drawn; i < upto; i++) html += this.card(list[i], eager && i < 4, i < 2);
      if (html) this.grid.insertAdjacentHTML('beforeend', html);
      this.drawn = upto;
      const left = list.length - upto;
      this.moreBtn.hidden = left <= 0;
      if (this.moreText) this.moreText.textContent = 'Showing ' + upto + ' of ' + list.length;
      this.moreText.hidden = left <= 0;
      this.total = list.length;
    }
    card(p, eager, high) {
      const sale = p.c > p.p;
      const url = ROOT + 'products/' + p.h;
      let img = '';
      if (p.i) {
        const w = (n) => p.i.replace(/width=\d+/, 'width=' + n);
        img =
          '<img class="ba-find__img" src="' + esc(w(450)) + '" srcset="' +
          [300, 450, 600, 800].map((n) => esc(w(n)) + ' ' + n + 'w').join(', ') +
          '" sizes="(min-width: 1200px) 280px, (min-width: 750px) 30vw, 50vw" alt="' + esc(p.t) + '" width="600" height="600"' +
          (eager ? ' loading="eager"' + (high ? ' fetchpriority="high"' : '') : ' loading="lazy"') + ' decoding="async">';
      }
      return (
        '<li class="ba-find__item"><a class="ba-find__card" href="' + esc(url) + '" data-h="' + esc(p.h) + '">' +
        '<div class="ba-find__media">' + (sale ? '<span class="ba-find__pill">Sale</span>' : '') + img + '</div>' +
        '<h3 class="ba-find__title">' + esc(p.t) + '</h3>' +
        '<div class="ba-find__prices"><span class="ba-find__price' + (sale ? ' ba-find__price--now' : '') + '">' + esc(p.pm) + '</span>' +
        (sale ? '<s class="ba-find__price ba-find__price--was">' + esc(p.cm) + '</s>' : '') +
        '</div></a></li>'
      );
    }
    activeCount() {
      const st = this.state;
      return (
        (st.g !== this.scope.g && st.g ? 1 : 0) +
        (st.c !== this.scope.c && st.c ? 1 : 0) +
        st.col.length +
        st.size.length +
        st.price.length +
        st.len.length
      );
    }
    syncControls() {
      const st = this.state;
      if (this.sortSel.value !== st.sort) this.sortSel.value = st.sort;
      if (this.sortLabel) this.sortLabel.textContent = SORTS[st.sort] || SORTS.best;
      const n = this.activeCount();
      this.fcount.textContent = n ? String(n) : '';
      this.fcount.hidden = !n;
      this.clearBtn.hidden = !this.input.value;
      /* removable chips for what is on */
      const chips = [];
      const chip = (rm, label) =>
        '<li><button type="button" class="ba-find__tag" data-rm="' + esc(rm) + '" aria-label="Remove ' + esc(label) + '">' +
        esc(label) + '<span aria-hidden="true" class="ba-find__x"></span></button></li>';
      if (st.g && st.g !== this.scope.g) chips.push(chip('g:' + st.g, st.g === 'w' ? "Women's" : "Men's"));
      if (st.c && st.c !== this.scope.c) chips.push(chip('c:' + st.c, this.catName(st.c, st.g)));
      for (const v of st.len) chips.push(chip('len:' + v, v.charAt(0).toUpperCase() + v.slice(1)));
      for (const v of st.col) chips.push(chip('col:' + v, FAM_NAME[v] || v));
      for (const v of st.size) chips.push(chip('size:' + v, 'Size ' + v));
      for (const v of st.price) {
        const b = this.priceBuckets().find((x) => x[0] === v);
        if (b) chips.push(chip('price:' + v, b[1]));
      }
      if (chips.length > 1) chips.push('<li><button type="button" class="ba-find__tag ba-find__tag--clear" data-rm="all:">Clear all</button></li>');
      this.activeRow.innerHTML = chips.join('');
      this.activeRow.hidden = !chips.length;
    }
    updateSummary() {
      if (!this.ix) return;
      let n;
      if (this.finding) n = this.total;
      else if (this.cfg.count && !this.scope.c && this.cfg.countIsScope) n = this.cfg.count;
      else n = this.ix.items.filter((p) => this.match(p, this.state)).length;
      if (n == null) return;
      this.summary.textContent = n + (n === 1 ? ' piece' : ' pieces');
    }

    /* the filter sheet */
    openSheet() {
      this.renderSheet();
      document.documentElement.classList.add('ba-find-lock');
      if (this.sheet.showModal) this.sheet.showModal();
      else this.sheet.setAttribute('open', '');
      this.sheetBody.scrollTop = 0;
      const ttl = this.sheet.querySelector('.ba-find__htitle');
      if (ttl) ttl.focus({ preventScroll: true });
      if (!this.ix) this.load().then(() => this.renderSheet()).catch(() => {});
    }
    closeSheet() {
      if (this.sheet.close) this.sheet.close();
      else this.sheet.removeAttribute('open');
      document.documentElement.classList.remove('ba-find-lock');
      if (this.finding) this.scrollToResults();
    }
    renderSheet() {
      if (!this.ix) {
        this.sheetBody.innerHTML = '<p class="ba-find__wait">One moment, gathering every piece.</p>';
        return;
      }
      const st = this.state;
      const f = this.facets();
      const focusKey = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.k + ':' + document.activeElement.dataset.v : '';
      const btn = (k, v, label, n, on, sw) =>
        '<button type="button" class="ba-find__opt' + (sw ? ' ba-find__opt--sw' : '') + '" data-k="' + k + '" data-v="' + esc(v) + '" aria-pressed="' + (on ? 'true' : 'false') + '"' +
        (!n && !on ? ' disabled' : '') + '>' +
        (sw ? '<span class="ba-find__sw" style="background:' + sw + '"></span>' : '') +
        '<span>' + esc(label) + '</span><span class="ba-find__n">' + n + '</span></button>';
      const group = (title, k, opts, multi, hideEmpty) => {
        const shown = opts.filter((o) => o[2] > 0 || (multi ? st[k].includes(o[0]) : st[k] === o[0]) || !hideEmpty);
        if (!shown.length) return '';
        return (
          '<fieldset class="ba-find__group"><legend class="ba-find__legend">' + title + '</legend><div class="ba-find__opts' + (k === 'size' ? ' ba-find__opts--size' : '') + '">' +
          shown.map((o) => btn(k, o[0], o[1], o[2], multi ? st[k].includes(o[0]) : st[k] === o[0], o[3])).join('') +
          '</div></fieldset>'
        );
      };
      const lenWorth = f.len.some((o) => o[2] > 0) && (st.c === 'dresses' || st.c === 'bottoms' || st.c === '' || st.len.length);
      this.sheetBody.innerHTML =
        group('Shop for', 'g', f.g, false, false) +
        group('Category', 'c', f.c, false, true) +
        (lenWorth ? group('Dress length', 'len', f.len, true, true) : '') +
        group('Color', 'col', f.col, true, true) +
        group('Size', 'size', f.size, true, true) +
        group('Price', 'price', f.price, true, true);
      const n = this.finding ? this.total : this.ix.items.filter((p) => this.match(p, st)).length;
      this.showBtn.textContent = 'Show ' + n + (n === 1 ? ' piece' : ' pieces');
      if (focusKey) {
        const [k, v] = focusKey.split(':');
        const el = this.sheetBody.querySelector('[data-k="' + k + '"][data-v="' + (window.CSS && CSS.escape ? CSS.escape(v) : v) + '"]');
        if (el) el.focus({ preventScroll: true });
      }
    }
  }

  /* small API for the header search (assets/ba-header.js): suggestions while typing */
  window.BAFind = {
    load: (count) => loadIndex(count, new Set()),
    suggest: async (q, max) => {
      const ix = await loadIndex(0, new Set());
      if (!q.trim()) return { items: [], total: 0 };
      const s = runSearch(q, ix);
      let list = ix.items.filter((p) => s.scores.has(p.h));
      if (s.pq.gender) list = list.filter((p) => p.g.includes(s.pq.gender));
      if (s.pq.maxPrice) list = list.filter((p) => p.p <= s.pq.maxPrice);
      list.sort((a, b) => (s.scores.get(b.h) || 0) - (s.scores.get(a.h) || 0) || a.rank - b.rank);
      return {
        total: list.length,
        fixed: s.pq.fixed.map((f) => f[1]).join(' '),
        items: list.slice(0, max || 6).map((p) => ({ url: ROOT + 'products/' + p.h, title: p.t, price: p.pm, was: p.c > p.p ? p.cm : '', img: p.i ? p.i.replace(/width=\d+/, 'width=160') : '' })),
      };
    },
  };

  /* ---------- Back to the same spot on the page's own grid (All products, Women's, Men's) ----------
     The stock grid loads 24 pieces at a time as the shopper scrolls, and a tapped card puts
     ?page=N#card in the address. When the browser reloads the page on Back (no back/forward
     cache), only page N came back and the shopper landed far from the card. So on leaving, the
     pages shown and the card on screen are kept for this tab; on Back the missing pages are put
     back and that card returns to the same place on the screen. Category views do the same in
     sections/ba-category.liquid. */
  const FIND_HASH = /(^|[#&])(q|g|c|col|size|price|len|sort|n)=/;
  const gridMemory = () => {
    const list = document.querySelector('results-list');
    const grid = list && list.querySelector('[ref="grid"]');
    if (!grid || document.querySelector('.ba-cat__wrap') || !document.querySelector('[data-ba-find]')) return;
    const ku = new URL(location.href);
    ['page', 'preview_theme_id', 'section_id'].forEach((k) => ku.searchParams.delete(k));
    const KEY = 'ba-grid:' + ku.pathname + ku.search;
    const cards = () => Array.from(grid.children).filter((li) => li.dataset && li.dataset.productId);
    const pg = (li) => Number(li && li.dataset.page) || 1;
    let tapped = null;
    let tappedAt = 0;
    grid.addEventListener(
      'click',
      (e) => {
        const li = e.target.closest && e.target.closest('li');
        if (li && li.parentElement === grid) (tapped = li), (tappedAt = Date.now());
      },
      true
    );
    window.addEventListener('pagehide', () => {
      const all = cards();
      if (!all.length || document.documentElement.classList.contains('ba-is-finding')) return;
      let li = tapped && tapped.isConnected && Date.now() - tappedAt < 20000 ? tapped : null;
      if (!li) li = all.find((x) => x.getBoundingClientRect().bottom > 80);
      const r = li ? li.getBoundingClientRect() : null;
      try {
        sessionStorage.setItem(
          KEY,
          JSON.stringify({ first: pg(all[0]), last: pg(all[all.length - 1]), y: Math.round(scrollY), pid: li ? li.dataset.productId : '', top: r ? Math.round(r.top) : 0, at: Date.now() })
        );
      } catch (e) {}
    });

    let back = false;
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      back = !!nav && nav.type === 'back_forward';
    } catch (e) {}
    if (!back || FIND_HASH.test(location.hash)) return;
    let rec = null;
    try {
      rec = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    } catch (e) {}
    const all = cards();
    if (!rec || !all.length || Date.now() - rec.at > 3 * 3600 * 1000) return;
    const curFirst = pg(all[0]);
    const curLast = pg(all[all.length - 1]);
    if (curFirst < rec.first || curLast > rec.last) return;
    try {
      history.scrollRestoration = 'manual';
    } catch (e) {}
    const lastPage = Number(grid.dataset.lastPage) || rec.last;
    const want = [];
    for (let p = rec.first; p <= Math.min(rec.last, lastPage); p++) if (p < curFirst || p > curLast) want.push(p);
    const head = all[0];
    const tail = all[all.length - 1];
    const hp = head.dataset.page;
    const tp = tail.dataset.page;
    /* keep the stock loader still while the pages go back in (it reads these two) */
    if (want.length) {
      head.dataset.page = '1';
      tail.dataset.page = String(lastPage);
    }
    let cancelled = false;
    ['touchstart', 'wheel', 'keydown'].forEach((t) => window.addEventListener(t, () => (cancelled = true), { once: true, passive: true }));
    const place = () => {
      if (cancelled) return;
      const li = rec.pid && grid.querySelector(':scope > li[data-product-id="' + String(rec.pid).replace(/[^\d]/g, '') + '"]');
      const y = li ? li.getBoundingClientRect().top + scrollY - rec.top : rec.y;
      window.scrollTo(0, Math.max(0, Math.round(y)));
    };
    const sid = list.getAttribute('section-id');
    const fetchPage = (p) => {
      const u = new URL(location.href);
      u.hash = '';
      u.searchParams.set('page', String(p));
      u.searchParams.set('section_id', sid);
      return fetch(u.toString(), { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[ref="grid"] > li')))
        .catch(() => null);
    };
    (async () => {
      if (want.length) {
        const got = await Promise.all(want.map(fetchPage));
        head.dataset.page = hp;
        tail.dataset.page = tp;
        const seen = new Set(cards().map((li) => li.dataset.productId));
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        want.forEach((p, i) => {
          for (const li of got[i] || []) {
            if (seen.has(li.dataset.productId)) continue;
            seen.add(li.dataset.productId);
            (p < curFirst ? before : after).appendChild(li);
          }
        });
        head.before(before);
        tail.after(after);
      }
      if (document.readyState === 'loading') await new Promise((r) => document.addEventListener('DOMContentLoaded', r, { once: true }));
      await new Promise((r) => requestAnimationFrame(() => r()));
      place();
      setTimeout(place, 250);
      setTimeout(place, 800);
      if (document.readyState !== 'complete') window.addEventListener('load', () => (place(), setTimeout(place, 300)), { once: true });
      /* if the stock "load more" marker is already on screen, show it again so it fires */
      setTimeout(() => {
        const s = list.querySelector('[ref="viewMoreNext"]');
        if (!s || cancelled || s.getBoundingClientRect().top > innerHeight + 200) return;
        const parent = s.parentNode;
        const next = s.nextSibling;
        s.remove();
        setTimeout(() => parent.insertBefore(s, next), 150);
      }, 1200);
    })();
  };

  let gridDone = false;
  const start = () => {
    if (!gridDone) {
      gridDone = true;
      try {
        gridMemory();
      } catch (e) {}
    }
    document.querySelectorAll('[data-ba-find]').forEach((el) => {
      if (!el.baFinder) el.baFinder = new Finder(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
